import { useRef, StatelessComponent } from 'react'
import * as moment from 'moment'
import { InternalPageProps } from 'interfaces/pages'
import CreditAccountsDetailsModuleContainer from 'components/features/pfm/CreditAccountsDetailsModule/CreditAccountsDetailsModuleContainer'
import CreditTotalsOverviewModuleContainer from 'components/features/pfm/CreditTotalsOverview/CreditTotalsOverviewModuleContainer'
import CreditAccountsModuleContainer from 'components/features/pfm/CategoriesModule/CreditAccountsModuleContainer'
import { SingleColumnLayout } from 'components/layouts/Layouts'
import ModuleWrapper from 'components/features/pfm/ModuleWrapper/ModuleWrapper'
import Anchor from 'components/atoms/navigation/Anchor'
import { getTimeframeLastMonth } from 'helpers/dateTimeHelpers'
import TrendRankContainer from 'components/features/pfm/TrendRank/TrendRankContainer'
import { NO_BALANCES_LABEL } from 'constants/accounts'
import PageFooterButton from 'components/atoms/buttons/PageFooterButton'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { PageParamsStickyHeader } from 'components/features/pfm/PageParamsHeader/PageParamsStickyHeader'
import { DesktopInternalStickyHeader } from 'components/features/pfm/DesktopInternalStickyHeader/DesktopInternalStickyHeader'
import { PageParamsHeader } from 'components/features/pfm/PageParamsHeader/PageParamsHeader'
import { useShouldShowPageParamsStickyHeader } from 'helpers/pageHelpers'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import styles from 'components/features/pfm/CreditPage/CreditPage.styl'

export type CreditPageProps = InternalPageProps & {
  isCreditFileLinked: boolean
}

const CREDIT_NOT_LINKED_LABEL = 'Your credit file is not linked'

const CreditPage: StatelessComponent<CreditPageProps> = (props) => {
  const { timeframe, isCreditFileLinked } = props
  const creditProfileDate = moment(getTimeframeLastMonth(timeframe)).format('MMM Y')

  const pageBody = (
    <>
      <TrendRankContainer type='creditscore' timeframe={ timeframe } />

      <ModuleWrapper
        title='Balances By Type'
        subtitle={ `(as of ${ creditProfileDate })` }
        anchor='creditscore_categories'
      >
        <CreditAccountsModuleContainer
          timeframe={ timeframe }
          piePlaceholderLabel={ isCreditFileLinked ? NO_BALANCES_LABEL : CREDIT_NOT_LINKED_LABEL }
        />
      </ModuleWrapper>

      <ModuleWrapper title='Credit File Summary' subtitle={ `(as of ${ creditProfileDate })` }>
        <CreditTotalsOverviewModuleContainer timeframe={ timeframe } />
      </ModuleWrapper>

      <ModuleWrapper
        title='Account Details'
        subtitle={ `(as of ${ creditProfileDate })` }
        card={ false }
        anchor='creditscore_details'
      >
        <CreditAccountsDetailsModuleContainer timeframe={ timeframe } />
      </ModuleWrapper>

      <PageFooterButton text='See Your Opportunities' to='opportunities' />
    </>
  )

  const staticHeaderRef = useRef<HTMLDivElement>(null)
  const shouldShowStickyHeader = useShouldShowPageParamsStickyHeader(staticHeaderRef)

  const notifications = (
    <NotificationTrayWrapper
      renderLevel='page'
      placement='creditscore_general'
      isPositionRelative
    />
  )

  return (
    <InternalLayoutContainer
      sidebar={ <InternalSidebarContainer /> }
      notifications={ notifications }
      stickyHeader={ <PageParamsStickyHeader type='creditscore' /> }
      shouldShowStickyHeader={ shouldShowStickyHeader }
    >
      <Anchor placement='creditscore'>
        <Anchor placement='creditscore_general'>
          <DesktopInternalStickyHeader isShown={ shouldShowStickyHeader }>
            <PageParamsStickyHeader type='creditscore' />
          </DesktopInternalStickyHeader>
          <SingleColumnLayout
            className={ styles.page }
            header={ <PageParamsHeader type='creditscore' nodeRef={ staticHeaderRef } /> }
          >
            { pageBody }
          </SingleColumnLayout>
        </Anchor>
      </Anchor>
    </InternalLayoutContainer>
  )
}

export default CreditPage
